import { useEffect, useMemo, useRef, useState } from "react";
import type { MouseEvent as ReactMouseEvent } from "react";
import type { ApiStatus, EasyModeOptions, FilmProject, ProjectGoal, SaveStatus, Scene } from "../types";
import { goalLabel } from "../utils/analysisSummary";
import {
  IMAGE_QUALITY_OPTIONS,
  IMAGE_SIZE_OPTIONS,
  charactersInScenes,
  resolveImageSize,
  scenesForEasyModeScope
} from "../utils/easyMode";
import { clampRuntimeSeconds, formatRuntime } from "../utils/storyboard";
import { Icon } from "./Icon";

interface TopbarProps {
  project: FilmProject;
  apiStatus: ApiStatus;
  saveStatus: SaveStatus;
  uploadSync: UploadSyncSummary;
  syncActionState?: "conflict" | "failed";
  onProjectChange: (changes: Partial<FilmProject>) => void;
  onSelectScene: (sceneId: string) => void;
  onOpenSetup: () => void;
  onOpenGenerate: () => void;
  onOpenSimple?: () => void;
  onEasyMode: () => void;
  easyModeOptions: EasyModeOptions;
  onEasyModeOptionsChange: (options: EasyModeOptions) => void;
  isEasyModeRunning?: boolean;
  onOpenReview: () => void;
  onCopyProjectLink: () => void;
  onOpenAssetLibrary: () => void;
  onRestoreCloudDraft?: () => void;
  onKeepBrowserDraft?: () => void;
  onRetryCloudSave?: () => void;
}

interface UploadSyncSummary {
  total: number;
  uploaded: number;
  pending: number;
  failed: number;
  localOnly: number;
}

interface ProjectSearchResult {
  scene: Scene;
  matchLabel: string;
  detail: string;
}

const projectGoals: ProjectGoal[] = [
  "trailer",
  "teaser",
  "sizzle_reel",
  "proof_of_concept",
  "previz_animatic",
  "concept_scene",
  "short_film",
  "broll_establishing",
  "vfx_plate",
  "social_cutdown",
  "pitch_reel",
  "festival_investor"
];

const runtimeOptions = [
  { label: "0:15", value: 15 },
  { label: "0:30", value: 30 },
  { label: "1:00", value: 60 },
  { label: "2:00", value: 120 },
  { label: "5:00", value: 300 },
  { label: "10:00", value: 600 },
  { label: "15:00", value: 900 }
];

function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

function saveStatusMeta(apiStatus: ApiStatus, saveStatus: SaveStatus, uploadSync: UploadSyncSummary) {
  if (uploadSync.failed) {
    return { tone: "failed", label: "Upload failed", detail: `${pluralize(uploadSync.failed, "source")} retryable · nothing lost` };
  }
  if (uploadSync.pending) {
    return { tone: "saving", label: "Upload pending", detail: `${pluralize(uploadSync.pending, "source")} syncing` };
  }
  if (uploadSync.localOnly) {
    return { tone: "local", label: "Local only", detail: `${pluralize(uploadSync.localOnly, "source")} browser-protected` };
  }
  if (apiStatus === "checking") {
    return { tone: "saving", label: "Checking API", detail: "Finding project storage" };
  }
  if (apiStatus !== "online") {
    return { tone: "local", label: "Local only", detail: "Browser autosave active" };
  }
  if (saveStatus === "Saving") return { tone: "saving", label: "Saving", detail: "Cloud sync pending" };
  if (saveStatus === "Saved") return { tone: "saved", label: "Cloud saved", detail: "Latest draft synced" };
  if (saveStatus === "Restore draft") return { tone: "warning", label: "Restore draft", detail: "Choose cloud or browser" };
  if (saveStatus === "Save failed") return { tone: "failed", label: "Cloud save failed", detail: "Retry or keep browser draft" };
  if (saveStatus === "Restoring") return { tone: "saving", label: "Restoring", detail: "Loading cloud draft" };
  return { tone: "local", label: "Local only", detail: "Browser autosave active" };
}

function includesQuery(value: string | undefined, query: string): boolean {
  return Boolean(value && value.toLowerCase().includes(query));
}

function frameSearchText(frame: Scene["frames"][number]): string {
  const assignments = frame.sourceAssignments;
  return [
    frame.label,
    frame.shotType,
    frame.prompt || "",
    frame.fileName || "",
    ...(assignments?.characterNames || []),
    ...(assignments?.voiceCharacterNames || [])
  ].join(" ");
}

function projectSearchResults(project: FilmProject, query: string): ProjectSearchResult[] {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return [];

  const beatsByScene = new Map(
    project.scenes.map((scene) => [
      scene.id,
      project.agentBeats.filter((beat) => beat.sceneId === scene.id)
    ])
  );

  return project.scenes.reduce<ProjectSearchResult[]>((results, scene) => {
    if (results.length >= 6) return results;
    const characterMatch = scene.characters.some((character) => includesQuery(character, normalizedQuery));
    const beatMatches = (beatsByScene.get(scene.id) || []).filter((beat) => (
      includesQuery(beat.label, normalizedQuery) || includesQuery(beat.purpose, normalizedQuery)
    ));
    const frameMatches = scene.frames.filter((frame) => includesQuery(frameSearchText(frame), normalizedQuery));
    const sceneMatch =
      includesQuery(scene.slugline, normalizedQuery) ||
      includesQuery(scene.synopsis, normalizedQuery) ||
      includesQuery(scene.location, normalizedQuery) ||
      includesQuery(scene.readiness, normalizedQuery) ||
      characterMatch;

    if (!sceneMatch && !beatMatches.length && !frameMatches.length) return results;

    const matchLabel = includesQuery(scene.slugline, normalizedQuery)
      ? "Slugline"
      : includesQuery(scene.location, normalizedQuery)
        ? "Location"
        : characterMatch
          ? "Character"
          : frameMatches.length
            ? "Board"
            : beatMatches.length
              ? "Beat"
              : "Scene";
    const detail = [
      scene.location,
      scene.characters.slice(0, 3).join(", "),
      `${scene.frames.length} boards`
    ].filter(Boolean).join(" · ");

    results.push({ scene, matchLabel, detail });
    return results;
  }, []);
}

export function Topbar({
  project,
  apiStatus,
  saveStatus,
  uploadSync,
  syncActionState,
  onProjectChange,
  onSelectScene,
  onOpenSetup,
  onOpenGenerate,
  onOpenSimple,
  onEasyMode,
  easyModeOptions,
  onEasyModeOptionsChange,
  isEasyModeRunning,
  onOpenReview,
  onCopyProjectLink,
  onOpenAssetLibrary,
  onRestoreCloudDraft,
  onKeepBrowserDraft,
  onRetryCloudSave
}: TopbarProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const status = saveStatusMeta(apiStatus, saveStatus, uploadSync);
  const runtimeSelectOptions = runtimeOptions.some((option) => option.value === project.runtimeSeconds)
    ? runtimeOptions
    : [{ label: `${formatRuntime(project.runtimeSeconds)} custom`, value: project.runtimeSeconds }, ...runtimeOptions];
  const searchResults = useMemo(() => projectSearchResults(project, searchQuery), [project, searchQuery]);
  const hasSearchQuery = searchQuery.trim().length > 0;
  const searchPanelId = "project-search-results";
  const easyScopeScenes = scenesForEasyModeScope(project.scenes, easyModeOptions.sceneScope);
  const easyScopeCharacters = charactersInScenes(project.characters, easyScopeScenes);
  const easyScopeBoards = easyScopeScenes.reduce((total, scene) => total + scene.frames.length, 0);
  const easyResolvedSize = resolveImageSize(easyModeOptions.imageSize, project.aspectRatio);
  const easyQualityHint = IMAGE_QUALITY_OPTIONS.find((option) => option.id === easyModeOptions.imageQuality)?.hint || "";

  const easyMenuRef = useRef<HTMLDetailsElement | null>(null);

  // Native <details> stays open until re-toggled; close the Easy Mode popover on outside click or Escape.
  useEffect(() => {
    function handlePointerDown(event: MouseEvent) {
      const details = easyMenuRef.current;
      if (!details?.open) return;
      if (event.target instanceof Node && details.contains(event.target)) return;
      details.removeAttribute("open");
    }
    function handleKeyDown(event: KeyboardEvent) {
      const details = easyMenuRef.current;
      if (!details?.open || event.key !== "Escape") return;
      details.removeAttribute("open");
    }
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  function runEasyModeFromPopover(event: ReactMouseEvent<HTMLButtonElement>) {
    event.currentTarget.closest("details")?.removeAttribute("open");
    onEasyMode();
  }

  function handleSelectSearchResult(sceneId: string) {
    onSelectScene(sceneId);
    setSearchQuery("");
    setSearchOpen(false);
  }

  return (
    <header className="topbar">
      <div className="project-title">
        <span>Project</span>
        <strong>{project.title}</strong>
        <div className={`topbar-save-status ${status.tone}${syncActionState ? " actionable" : ""}`} role="status" aria-live="polite">
          <span aria-hidden="true" />
          <small>{status.label}</small>
          <em>{status.detail}</em>
          {uploadSync.total ? (
            <div className="topbar-sync-pills" aria-label="Source upload sync">
              {uploadSync.uploaded ? <b>{uploadSync.uploaded} cloud</b> : null}
              {uploadSync.localOnly ? <b className="local">{uploadSync.localOnly} local</b> : null}
              {uploadSync.pending ? <b className="pending">{uploadSync.pending} pending</b> : null}
              {uploadSync.failed ? <b className="failed">{uploadSync.failed} failed</b> : null}
            </div>
          ) : null}
          {syncActionState === "conflict" ? (
            <div className="topbar-save-actions" role="group" aria-label="Resolve draft conflict">
              <button type="button" onClick={onRestoreCloudDraft}>Restore Cloud</button>
              <button type="button" onClick={onKeepBrowserDraft}>Keep Browser</button>
            </div>
          ) : syncActionState === "failed" ? (
            <div className="topbar-save-actions" role="group" aria-label="Retry cloud autosave">
              <button type="button" onClick={onKeepBrowserDraft}>Browser Only</button>
              <button type="button" onClick={onRetryCloudSave}>Retry Cloud</button>
            </div>
          ) : uploadSync.failed ? (
            <div className="topbar-save-actions" role="group" aria-label="Resolve failed uploads">
              <button type="button" onClick={onOpenAssetLibrary}>Open Assets</button>
            </div>
          ) : null}
        </div>
      </div>
      <details className="project-chip-menu">
        <summary className="project-chip" aria-label="Edit project goal and output settings">
          <span aria-hidden="true" />
          <strong>{goalLabel(project.goal)}</strong>
          <small>{formatRuntime(project.runtimeSeconds)} · {project.aspectRatio} · {project.resolution} test</small>
          <Icon name="chevron" />
        </summary>
        <div className="project-chip-popover" aria-label="Project goal and output settings">
          <label>
            <span>Goal</span>
            <select value={project.goal} onChange={(event) => onProjectChange({ goal: event.target.value as ProjectGoal })}>
              {projectGoals.map((goal) => (
                <option key={goal} value={goal}>
                  {goalLabel(goal)}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Runtime</span>
            <div className="runtime-controls compact">
              <select value={project.runtimeSeconds} onChange={(event) => onProjectChange({ runtimeSeconds: Number(event.target.value) })}>
                {runtimeSelectOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <input
                aria-label="Custom project runtime seconds"
                min={1}
                onChange={(event) => onProjectChange({ runtimeSeconds: clampRuntimeSeconds(Number(event.target.value), project.runtimeSeconds) })}
                step={1}
                type="number"
                value={project.runtimeSeconds}
              />
            </div>
          </label>
          <label>
            <span>Frame</span>
            <select value={project.aspectRatio} onChange={(event) => onProjectChange({ aspectRatio: event.target.value as FilmProject["aspectRatio"] })}>
              <option value="16:9">16:9 widescreen</option>
              <option value="2.39:1">2.39:1 letterbox</option>
              <option value="1:1">1:1 square</option>
              <option value="4:5">4:5 social</option>
              <option value="9:16">9:16 vertical</option>
            </select>
          </label>
          <label>
            <span>Quality</span>
            <select value={project.resolution} onChange={(event) => onProjectChange({ resolution: event.target.value as FilmProject["resolution"] })}>
              <option value="480p">480p test</option>
              <option value="720p">720p review</option>
              <option value="1080p">1080p polish</option>
            </select>
          </label>
        </div>
      </details>
      <div
        className={`search-box ${searchOpen && hasSearchQuery ? "search-open" : ""}`}
        onBlur={(event) => {
          const nextTarget = event.relatedTarget;
          if (nextTarget instanceof Node && event.currentTarget.contains(nextTarget)) return;
          setSearchOpen(false);
        }}
      >
        <Icon name="script" />
        <input
          type="search"
          placeholder="Search project..."
          aria-label="Search project"
          aria-controls={searchPanelId}
          aria-expanded={searchOpen && hasSearchQuery}
          value={searchQuery}
          onChange={(event) => {
            setSearchQuery(event.target.value);
            setSearchOpen(true);
          }}
          onFocus={() => setSearchOpen(true)}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              setSearchOpen(false);
              setSearchQuery("");
            }
          }}
        />
        {searchOpen && hasSearchQuery ? (
          <div className="project-search-results" id={searchPanelId} aria-label="Project search results">
            {searchResults.length ? searchResults.map(({ scene, matchLabel, detail }) => (
              <button
                className="project-search-result"
                key={scene.id}
                aria-label={`Scene ${String(scene.number).padStart(2, "0")}, ${scene.slugline}, ${matchLabel}, ${detail}`}
                type="button"
                onClick={() => handleSelectSearchResult(scene.id)}
              >
                <span>
                  <strong>{`Scene ${String(scene.number).padStart(2, "0")}`}</strong>
                  <small>{matchLabel}</small>
                </span>
                <b>{scene.slugline}</b>
                <em>{detail}</em>
              </button>
            )) : (
              <div className="project-search-empty" role="status">
                No project matches
              </div>
            )}
          </div>
        ) : null}
      </div>
      <div className="top-actions">
        {onOpenSimple ? (
          <button className="secondary-action compact-action" onClick={onOpenSimple} type="button" title="Switch to the new simplified Kinetix">
            <Icon name="spark" />
            New Look
          </button>
        ) : null}
        <button className="secondary-action compact-action" onClick={onCopyProjectLink} type="button">
          <Icon name="link" />
          Copy Link
        </button>
        <button className="secondary-action" onClick={onOpenSetup} type="button">
          <Icon name="script" />
          Setup
        </button>
        <div className="easy-mode-split">
          <button
            className="easy-mode-action"
            onClick={onEasyMode}
            type="button"
            disabled={isEasyModeRunning}
            aria-busy={isEasyModeRunning}
            title="Auto-pilot: generate character cards, assign voices, and build the storyboard from your script"
          >
            <Icon name="spark" />
            {isEasyModeRunning ? "Easy Mode…" : "Easy Mode"}
          </button>
          <details className="easy-mode-menu" ref={easyMenuRef}>
            <summary aria-label="Easy Mode scope and quality options">
              <Icon name="chevron" />
            </summary>
            <div className="easy-mode-popover" aria-label="Easy Mode setup">
              <header>
                <strong>✨ Easy Mode</strong>
                <small>One tap: voices → cards → plates → storyboard</small>
              </header>
              <label className="easy-mode-field">
                <span>Scope</span>
                <select
                  value={easyModeOptions.sceneScope}
                  onChange={(event) => onEasyModeOptionsChange({ ...easyModeOptions, sceneScope: event.target.value })}
                >
                  <option value="all">All scenes ({project.scenes.length})</option>
                  {project.scenes.map((scene) => (
                    <option key={scene.id} value={scene.id}>
                      Scene {String(scene.number).padStart(2, "0")} · {scene.slugline}
                    </option>
                  ))}
                </select>
                <small>
                  {easyScopeScenes.length} scene{easyScopeScenes.length === 1 ? "" : "s"} · {easyScopeCharacters.length} character{easyScopeCharacters.length === 1 ? "" : "s"} · {easyScopeBoards} board{easyScopeBoards === 1 ? "" : "s"}
                </small>
              </label>
              <div className="easy-mode-field">
                <span>Image quality</span>
                <div className="easy-mode-segmented" role="group" aria-label="Image quality">
                  {IMAGE_QUALITY_OPTIONS.map((option) => (
                    <button
                      className={easyModeOptions.imageQuality === option.id ? "active" : ""}
                      key={option.id}
                      onClick={() => onEasyModeOptionsChange({ ...easyModeOptions, imageQuality: option.id })}
                      type="button"
                      aria-pressed={easyModeOptions.imageQuality === option.id}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                <small>{easyQualityHint}</small>
              </div>
              <label className="easy-mode-field">
                <span>Frame size</span>
                <select
                  value={easyModeOptions.imageSize}
                  onChange={(event) => onEasyModeOptionsChange({ ...easyModeOptions, imageSize: event.target.value as EasyModeOptions["imageSize"] })}
                >
                  {IMAGE_SIZE_OPTIONS.map((option) => (
                    <option key={option.id} value={option.id}>{option.label}</option>
                  ))}
                </select>
                <small>{easyModeOptions.imageSize === "auto" ? `${project.aspectRatio} → ${easyResolvedSize.replace("x", "×")}` : "Fixed OpenAI image size"}</small>
              </label>
              <p className="easy-mode-note">🔒 Uploads &amp; locked items always win — Easy Mode only fills empty slots.</p>
              <button
                className="easy-mode-action easy-mode-run"
                onClick={runEasyModeFromPopover}
                type="button"
                disabled={isEasyModeRunning}
              >
                <Icon name="spark" />
                {isEasyModeRunning ? "Easy Mode…" : "Run Easy Mode"}
              </button>
            </div>
          </details>
        </div>
        <button className="secondary-action" onClick={onOpenGenerate} type="button">
          <Icon name="spark" />
          Generate Boards
        </button>
        <button className="primary-action" onClick={onOpenReview} type="button">
          <Icon name="play" />
          Review EvoLink
        </button>
      </div>
    </header>
  );
}
